// Single source of truth for Oneiros website content.
// Copy, claims, addresses, phones and imagery are sourced from the official
// website (cluboneiros.com) or club-supplied material (Varachha leaflet + owner chat). Nothing invented.

const img = (name) => `/oneiros/${name}`;

export const IMAGES = {
  logoWhite: img("logo-white.webp"),
  anthemPoster: img("poster-anthem.jpg"),
};

export const ANTHEM = {
  poster: img("poster-anthem.jpg"),
  src: "/oneiros/anthem-720.mp4",
  audio: "/oneiros/anthem-audio.mp3",
  remote:
    "https://cluboneiros.com/cdn/shop/videos/c/vp/ea65f169af50487ead52a6dbc219e303/ea65f169af50487ead52a6dbc219e303.HD-1080p-7.2Mbps-67975521.mp4?v=0",
};

export const WHATSAPP = {
  number: "917690094003",
  defaultMessage: "Hi Oneiros! I'd like to know more about memberships at Oneiros Sports Club.",
  link: (message) =>
    `https://wa.me/${WHATSAPP.number}?text=${encodeURIComponent(message || WHATSAPP.defaultMessage)}`,
};

export const SALES_EMAIL = "sales@cluboneiros.com";
export const KATARGAM_MAP_URL = "https://share.google/yrLvYE36o94WIreKE";
export const ADAJAN_MAP_URL = "https://share.google/DvStglEQ2QSTBuYZm";
export const VARACHHA_MAP_URL = "https://share.google/z8C24B3wIXGAhNieG";

// Oneiros Siri voice config — English only, Indian female voice preferred
export const SIRI = {
  welcome:
    "Namaste, and welcome to Oneiros Sports Club! I'm Oneiros Siri, your club concierge. I can show you our clubs, take you to any activity, or help your family of four choose between Blue and Gold membership. How may I help you today?",
};

// The Oneiros mascots — each hosts its own club page as the face of Oneiros Siri.
export const MASCOTS = {
  home: {
    key: "home",
    name: "Oneiros Trio",
    src: img("mascots/mascots-trio.png"),
    bubble:
      "Hi! We're the Oneiros mascots — Orange, White and Blue. Varachha pre-sale is live with the Blue Moon Offer. Ask us anything about the club!",
  },
  katargam: {
    key: "katargam",
    name: "Orange One",
    src: img("mascots/mascot-katargam.png"),
    bubble:
      "Namaste! I'm the Orange One, your guide to Oneiros Katargam — Asia's Strongest Gym. Shall I show you around?",
  },
  adajan: {
    key: "adajan",
    name: "White One",
    src: img("mascots/mascot-adajan.png"),
    bubble:
      "Hello! I'm the White One of Oneiros Adajan — the flagship experience. What would you like to see first?",
  },
  varachha: {
    key: "varachha",
    name: "Blue One",
    src: img("mascots/mascot-varachha.png"),
    bubble:
      "Hi, I'm the Blue One! Oneiros Varachha pre-sale is live — the Blue Moon Offer for our first 500 members is on!",
  },
};

export const VARACHHA_PRESALE = {
  eyebrow: "Pre-Sale Live Now",
  titleTop: "Oneiros",
  titleAccent: "Varachha",
  titleBottom: "Pre-Sale Is Live.",
  copyLead:
    "The newest Oneiros destination is now open for pre-sale in Varachha. The Blue Moon Offer rewards our first 500 members — ",
  copyHighlight: "NO AMC for life.",
  copyTail: " Once in a blue moon, truly.",
  offerName: "Blue Moon Offer",
  offerNote: "First 500 Members: No AMC",
  cta: "Claim the Blue Moon Offer",
  to: "/clubs/varachha",
  mascot: img("mascots/mascot-varachha.png"),
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Our Clubs", to: "/our-clubs" },
  { label: "Facilities", to: "/facilities" },
  { label: "Membership", to: "/blue-membership" },
  { label: "Community", to: "/community" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const MARQUEE_WORDS = [
  "Fitness",
  "Sports",
  "Wellness",
  "Community",
  "Performance",
  "Lifestyle",
  "Family",
  "Recovery",
];

export const CLUBS = [
  {
    id: "katargam",
    index: "01",
    name: "Oneiros Katargam",
    shortName: "Katargam",
    positioning: "Surat's First of Its Kind Sports Club",
    eyebrow: "Performance & Sports Destination",
    description:
      "A holistic sports and wellness destination crafted for modern lifestyles — high-performance training, indoor and outdoor sport, and family-focused activity zones under one roof.",
    uspLabel: "Katargam USPs",
    highlights: [
      "Asia's Strongest Gym",
      "First & Only Indoor Semi-Olympic Swimming Pool in Surat",
      "Prakash Padukone International Badminton Academy",
      "European Style Fitness Studio",
    ],
    image: img("katargam-ext.jpg"),
    address:
      "Opposite Manibag Party Plot, Ved Road, Near Niru Farm, Surat, Gujarat",
    mapUrl: KATARGAM_MAP_URL,
    timings: "Morning 6 AM – 10 PM",
    phone: "+91 63518 97993",
    phoneHref: "tel:+916351897993",
    activities: [
      { name: "Gym", image: img("kat-gym.jpg") },
      { name: "Fitness Studio", image: img("kat-fitness.jpg") },
      { name: "Swimming", image: img("kat-swimming.png") },
      { name: "Kids Club", image: img("kat-kids-club.jpg") },
      { name: "Yoga", image: img("kat-yoga.png") },
      { name: "Badminton", image: img("kat-badminton.jpg") },
      { name: "Karate", image: img("fac-karate.jpg") },
      { name: "Pickleball", image: img("fac-pickleball.jpg") },
    ],
  },
  {
    id: "adajan",
    index: "02",
    name: "Oneiros Adajan",
    shortName: "Adajan",
    positioning: "The Flagship Experience",
    eyebrow: "Family Lifestyle Club",
    description:
      "Where lifestyle meets leisure — Surat's largest gym floor, refined studios, expansive sports and recovery spaces, designed for those who value balance as much as performance.",
    uspLabel: "Adajan USPs",
    highlights: [
      "Surat's Largest Gym Floor",
      "The Heaviest 350 kg Training Tyre",
      "Prakash Padukone International Badminton Academy",
      "The Official Sports Club Anthem",
    ],
    image: img("adajan-ext.jpg"),
    address:
      "Opp McDonald's, Near Riverdale Circle (TGB Circle), L.P. Savani Road, Adajan, Surat, Gujarat 395009",
    mapUrl: ADAJAN_MAP_URL,
    timings: "Morning 6 AM – 11 PM",
    phone: "+91 76900 94003",
    phoneHref: "tel:+917690094003",
    activities: [
      { name: "Gym", image: img("adajan-gym.jpg") },
      { name: "Fitness Studio", image: img("adajan-fitness.jpg") },
      { name: "Swimming", image: img("adajan-swimming.jpg") },
      { name: "Kids Arena", image: img("adajan-kids-arena.jpg") },
      { name: "Badminton", image: img("kat-badminton.jpg") },
      { name: "Lawn Tennis", image: img("adajan-lawn-tennis.jpg") },
      { name: "Pickleball", image: img("fac-pickleball.jpg") },
      { name: "Volleyball", image: img("adajan-volleyball.jpg") },
    ],
  },
  {
    id: "varachha",
    index: "03",
    name: "Oneiros Varachha",
    shortName: "Varachha",
    positioning: "A New Chapter for Oneiros in Surat",
    eyebrow: "Now Open — The Newest Destination",
    description:
      "A new Oneiros destination bringing the brand's philosophy of fitness, sports, family and community closer to Varachha — with a launch offer the club calls a Once in a Blue Moon moment.",
    uspLabel: "Varachha USPs",
    highlights: [
      "Now Open in Varachha",
      "First 500 Members Get NO AMC",
      "Gold Members: Access to All Three Clubs",
    ],
    image: img("varachha-outer.png"),
    address: "Siraj Dokadia Road, Behind SMIMER Hospital, Bombay Market-Punagam Rd, No 1, Umarwada, Surat, Gujarat 395010",
    addressNote: "Varachha, Surat — see exact location on map",
    mapUrl: VARACHHA_MAP_URL,
    timings: "9:00 AM to 7:30 PM",
    phone: "+91 75749 42000",
    phoneHref: "tel:+917574942000",
    phone2: "+91 75749 43000",
    email: "varachha@cluboneiros.com",
    activities: [],
  },
];

// Varachha experience categories — from the club's official leaflet
export const VARACHHA_EXPERIENCE = [
  { title: "Fitness", tagline: "Build a stronger you.", items: ["Premium Gym", "Sky Deck Run", "Swimming Pool", "Fitness Studio", "Karate", "Yoga"] },
  { title: "Sports", tagline: "Play. Compete. Grow.", items: ["Cricket Arena", "Lawn Tennis", "Pickleball", "Badminton", "Table Tennis", "Skating", "Squash", "Kids Club"] },
  { title: "Wellness & Recovery", tagline: "Reset. Recharge. Repeat.", items: ["Meditation Sphere", "Steam", "Sauna", "Jacuzzi", "Ice Shower"] },
  { title: "Leisure & Entertainment", tagline: "Relax. Unwind. Connect.", items: ["Audio-Video Library", "Arcade Games", "Kids Arena", "Board Games", "Pool"] },
];

// Two memberships — Blue (one specific club) and Gold (all three clubs)
export const MEMBERSHIPS = {
  blue: {
    id: "blue",
    name: "Blue Membership",
    tagline: "Your club. Your family. Your years.",
    scope: "Single-club membership — choose your Oneiros",
    familyNote: "Designed around one family of four",
    cardImage: img("blue-card.jpg"),
    cardImageAlt: "Oneiros Blue Membership card",
    tenures: [
      { club: "Oneiros Katargam", short: "Katargam", years: "12 Years" },
      { club: "Oneiros Adajan", short: "Adajan", years: "15 Years" },
      { club: "Oneiros Varachha", short: "Varachha", years: "25 Years" },
    ],
    benefits: [
      "Full membership at your chosen club — Katargam, Adajan or Varachha",
      "Access to that club's core sports and fitness facilities",
      "Expert-led group classes for all four family members",
      "Recovery & wellness access at your home club",
      "Sports access — courts, pools and arenas at your club",
      "Special member events for the whole family",
    ],
  },
  gold: {
    id: "gold",
    name: "Gold Membership",
    tagline: "Every Oneiros door opens.",
    scope: "All three clubs under one signature",
    familyNote: "For families who want it all",
    cardImage: img("gold-card.jpg"),
    cardImageAlt: "Oneiros Gold Membership card",
    benefits: [
      "Access to ALL clubs — Varachha, Adajan & Katargam",
      "Priority bookings",
      "Guest privileges",
      "Exclusive lounges",
      "Special events",
    ],
  },
  launchOffer: "Once in a Blue Moon Offer — the first 500 members get NO AMC. This will never happen again.",
};

export const FAMILY_FOUR = [
  { who: "Your First Child", copy: "Game identification, kids arenas and sports nurturing — they find their sport early." },
  { who: "Your Second Star", copy: "Swimming, skating, karate — every visit builds confidence and coordination." },
  { who: "You & Your Partner", copy: "Strength, studio classes, courts and recovery — training that fits busy lives." },
];

export const FACILITIES = [
  { id: "gym", name: "Gym", category: "Performance", image: img("fac-gym.jpg"), description: "A high-performance strength and conditioning space equipped with world-class machines and free weights." },
  { id: "swimming", name: "Swimming", category: "Sports", image: img("fac-swimming.jpg"), description: "A well-maintained pool for learning, endurance and joint-friendly workouts." },
  { id: "fitness-studio", name: "Fitness Studio", category: "Performance", image: img("fac-fitness-studio.jpg"), description: "Yoga, Zumba, HIIT and functional training." },
  { id: "lawn-tennis", name: "Lawn Tennis", category: "Sports", image: img("fac-lawn-tennis.jpg"), description: "State-of-the-art court with professional coaching." },
  { id: "kids-arena", name: "Kids Arena", category: "Family", image: img("fac-kids-arena.jpg"), description: "Designed to build confidence and develop young athletes." },
  { id: "badminton", name: "Badminton", category: "Sports", image: img("kat-badminton.jpg"), description: "State-of-the-art courts and professional coaching." },
  { id: "pickleball", name: "Pickleball", category: "Sports", image: img("fac-pickleball.jpg"), description: "Professional courts for all skill levels." },
  { id: "karate", name: "Karate", category: "Sports", image: img("fac-karate.jpg"), description: "Strength, self-defense, flexibility and mental focus." },
  { id: "skating", name: "Skating", category: "Sports", image: img("fac-skating.jpg"), description: "Balance, coordination and endurance." },
  { id: "kids-club", name: "Kids Club", category: "Family", image: img("fac-kids-club.jpg"), description: "Game identification and sports nurturing program." },
  { id: "volleyball", name: "Volleyball", category: "Sports", image: img("adajan-volleyball.jpg"), description: "High-energy sessions for all skill levels." },
  { id: "box-cricket", name: "Box Cricket", category: "Sports", image: img("fac-box-cricket.jpg"), description: "Enclosed turf for team spirit, agility and competitive play." },
  { id: "recovery-arena", name: "Recovery Arena", category: "Lifestyle", image: img("fac-recovery.jpg"), description: "Relaxation, healing and muscle recovery." },
  { id: "indoor-games", name: "Indoor Games", category: "Family", image: img("fac-indoor-games.jpg"), description: "Table Tennis, Pool, Chess, Carrom and Foosball." },
  { id: "cafe", name: "Cafe", category: "Lifestyle", image: img("fac-cafe.jpg"), description: "Nutritious and delicious meals for a healthy lifestyle." },
  { id: "banquet", name: "Banquet", category: "Lifestyle", image: img("fac-banquet.jpg"), description: "Space for weddings, receptions and celebrations." },
];

export const TESTIMONIALS = [
  {
    id: "rakhi",
    name: "Dr. Rakhi Mehta",
    quote:
      "What I love most about Oneiros is that it's perfect for the entire family. While I train in the gym, my kids enjoy their activities and sports. It's rare to find a place that caters so well to every age group.",
  },
  {
    id: "robin",
    name: "Mr. Robin",
    quote:
      "Oneiros Sports Club is not just a gym, it's a complete lifestyle space. From world-class fitness equipment to sports and recovery zones, everything is thoughtfully designed. It genuinely motivates me to stay consistent and push my limits.",
  },
  {
    id: "shikha",
    name: "Shikha Shah",
    quote:
      "The scale and quality of Oneiros is unmatched. The gym floor, sports facilities, and overall ambience make it feel truly premium. You can instantly tell this is a club built for serious fitness and long-term wellness.",
  },
  {
    id: "dipen",
    name: "Mr. Dipen Lakdawala",
    quote:
      "Training at Oneiros has completely transformed my fitness journey. The environment keeps me motivated, the facilities are top-notch, and every workout feels purposeful. It's easily the best decision I've made for my health.",
  },
];

export const CONTACT = {
  email: SALES_EMAIL,
  clubs: [
    {
      id: "adajan",
      name: "Adajan",
      address:
        "Opp McDonald's, Near Riverdale Circle (TGB Circle), L.P. Savani Road, Adajan, Surat, Gujarat 395009",
      mapUrl: ADAJAN_MAP_URL,
      phone: "+91 76900 94003",
      phoneHref: "tel:+917690094003",
      timings: "Morning 6 AM – 11 PM",
    },
    {
      id: "katargam",
      name: "Katargam",
      address:
        "Opposite Manibag Party Plot, Ved Road, Near Niru Farm, Surat, Gujarat",
      mapUrl: KATARGAM_MAP_URL,
      phone: "+91 63518 97993",
      phoneHref: "tel:+916351897993",
      timings: "Morning 6 AM – 10 PM",
    },
    {
      id: "varachha",
      name: "Varachha",
      address: "Siraj Dokadia Road, Behind SMIMER Hospital, Bombay Market-Punagam Rd, No 1, Umarwada, Surat, Gujarat 395010",
      addressNote: "Varachha, Surat — see exact location on map",
      mapUrl: VARACHHA_MAP_URL,
      phone: "+91 75749 42000",
      phoneHref: "tel:+917574942000",
      email: "varachha@cluboneiros.com",
      timings: "9:00 AM to 7:30 PM",
    },
  ],
};

export const EVENTS_SAMPLE = [
  { id: "ev1", category: "Fitness Event", name: "Morning Fitness Sessions", date: "Schedule updating soon", time: "—", location: "Oneiros Clubs, Surat", description: "Group fitness sessions for members across our clubs. Calendar details are being updated by the club team." },
  { id: "ev2", category: "Sports Event", name: "Court Sport Fixtures", date: "Schedule updating soon", time: "—", location: "Oneiros Clubs, Surat", description: "Badminton, tennis and pickleball fixtures for members. Calendar details are being updated by the club team." },
  { id: "ev3", category: "Wellness", name: "Mind & Body Sessions", date: "Schedule updating soon", time: "—", location: "Oneiros Clubs, Surat", description: "Yoga and recovery-focused sessions. Calendar details are being updated by the club team." },
  { id: "ev4", category: "Family", name: "Family Activity Days", date: "Schedule updating soon", time: "—", location: "Oneiros Clubs, Surat", description: "Active days designed for every generation of the family. Calendar details are being updated by the club team." },
  { id: "ev5", category: "Community", name: "5L Kgs Community Milestones", date: "Schedule updating soon", time: "—", location: "Surat", description: "Community checkpoints of the 5,00,000 kilo city mission. Calendar details are being updated by the club team." },
  { id: "ev6", category: "Tournament", name: "Club Tournaments", date: "Schedule updating soon", time: "—", location: "Oneiros Clubs, Surat", description: "Competitive tournaments across sports. Calendar details are being updated by the club team." },
];

export const PILLARS = ["Fitness", "Sports", "Wellness", "Family", "Community", "Performance"];

export const ENRICHED = {
  anthemTitle: "India's First-Ever Sports Club Anthem",
  anthemCopy:
    "A landmark anthem created to represent strength, sportsmanship, and unity. The official sound that defines the spirit and identity of Oneiros.",
  liftTitle: "Built at Oneiros: A Champion's Record-Breaking 300 Kgs Lift",
  liftCopy:
    "Oneiros is known across India for its massive gym floor, heaviest dumbbells and international-standard equipment — built for athletes and fitness enthusiasts who demand more.",
  mission5l:
    "Oneiros is on a mission to help Surat collectively lose 5,00,000 kilos — by combining expert fitness training, smart nutrition and strong community support. Every member's progress contributes to a bigger goal. Because together, we're building a stronger Surat.",
  aspiration:
    "We aim to set a precedent in the fitness industry by guiding people to shed 5,00,000 kg of human body weight and create a Guinness Book of World Records — and to bring at least one Olympic medal for the country in indoor sports by 2026.",
  transformation:
    "The moment he stopped waiting and started working — everything changed. With structured training, expert guidance and the right nutrition support from Oneiros, Jesus Mehta transformed not just his body, but his confidence, mindset and daily lifestyle. Transformation doesn't start with motivation — it starts with commitment.",
};

export const INSTAGRAM = {
  handle: "oneiros_sportsclub",
  url: "https://www.instagram.com/oneiros_sportsclub/",
  posts: [
    { id: "ig1", img: img("kat-lifestyle.jpg"), caption: "Club life, every day. #Oneiros #RedefineYourRoutine" },
    { id: "ig2", img: img("kat-pool.jpg"), caption: "Surat's first indoor semi-Olympic pool. #Swimming" },
    { id: "ig3", img: img("strongest-gym.jpg"), caption: "Asia's Strongest Gym. #BuiltToPerform" },
    { id: "ig4", img: img("adajan-kids-arena.jpg"), caption: "Where kids find their game. #KidsArena" },
    { id: "ig5", img: img("kat-badminton.jpg"), caption: "Padukone Academy training nights. #Badminton" },
    { id: "ig6", img: img("fac-cafe.jpg"), caption: "Refuel the right way. #OneirosCafe" },
  ],
};
